/**
 * PropDocs deal-document status → DOCX-native editor lifecycle status.
 *
 * `<DocxEditor documentStatus>` models three lifecycle states — 'draft' (fully
 * editable), 'signing' and 'executed' (both LOCK the document: empty toolbar +
 * read-only). PropDocs has a richer AASM set (drafting/shared/reviewing/
 * preparing_to_sign/signing/executed/terminated, plus the UI-only
 * 'uploaded' and 'Approval Pending'), so this maps many → three.
 *
 * The rule mirrors the CKEditor path exactly (`DocumentEditor.isEditorDisabled`),
 * which keeps the editor writable only in DRAFT / UPLOADED / REVIEWING. Every
 * other state is a lock; we pick 'executed' for terminal states and 'signing'
 * for the signature pipeline so the editor's banner reads correctly.
 */

import { DocumentStatusEnum } from '../../../../shared/constants/document';

export type DocxEditorLifecycleStatus = 'draft' | 'signing' | 'executed';

/** States in which a deal document remains editable (parity with CKEditor). */
const EDITABLE_STATUSES: ReadonlySet<string> = new Set<string>([
  DocumentStatusEnum.DRAFT,
  DocumentStatusEnum.UPLOADED,
  DocumentStatusEnum.REVIEWING,
]);

/** Terminal / post-signature states — locked, and not part of the signing flow. */
const TERMINAL_STATUSES: ReadonlySet<string> = new Set<string>([
  DocumentStatusEnum.EXECUTED,
  DocumentStatusEnum.TERMINATED,
]);

/**
 * Map a PropDocs document status to the editor's lifecycle status.
 *
 * Unknown/absent status is treated as LOCKED ('executed') rather than editable —
 * failing closed is the safe default for a legal document surface.
 */
export function toDocxEditorStatus(status: string | null | undefined): DocxEditorLifecycleStatus {
  if (!status) return 'executed';
  if (EDITABLE_STATUSES.has(status)) return 'draft';
  if (TERMINAL_STATUSES.has(status)) return 'executed';
  // shared, preparing_to_sign, signing, Approval Pending → signature/approval
  // pipeline: locked, but not terminal.
  return 'signing';
}

/** True when the deal document's status permits editing at all. */
export function isDealDocumentEditable(status: string | null | undefined): boolean {
  return !!status && EDITABLE_STATUSES.has(status);
}
